// exp_PORTFOLIO-58018_file
import { FC, useMemo, useRef, useState } from 'react';

import vrPackageFormFieldTouch, {
    GeoSource,
} from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/vr_package/vr_package_form_field_touch';
import { useStaticDataProvider } from '@hh.ru/magritte-common-data-provider';
import {
    Button,
    ChipsInput,
    ChipsTriggerState,
    Flex,
    NavBar,
    SearchInput,
    Text,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24, QuestionCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { TooltipCommon } from 'components/common/TooltipCommon/TooltipCommon';

import { Region, selectRegions } from 'store/autoReducers';
import {
    selectVrCampaignAreas,
    selectVrCampaignErrors,
    selectVrCampaignVacancy,
    setVrCampaignAreas,
    setVrCampaignErrors,
} from 'store/models/vrCampaign';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'VrCampaignBuilderPage.areas.title',
    tooltip: 'VrCampaignBuilderPage.areas.tooltip',
    placeholder: 'VrCampaignBuilderPage.areas.placeholder',
    error: 'VrCampaignBuilderPage.areas.error',
    searchPlaceholder: 'VrCampaignBuilderPage.areas.searchPlaceholder',
    apply: 'VrCampaignBuilderPage.areas.apply',
    reset: 'VrCampaignBuilderPage.areas.reset',
    emptyTitle: 'VrCampaignBuilderPage.areas.empty.title',
    emptyDescription: 'VrCampaignBuilderPage.areas.empty.description',
};

interface AreaOption {
    value: string;
    text: string;
}

const flattenRegions = (regions: Region[]): AreaOption[] =>
    regions.flatMap((region) => [
        { value: region.id, text: region.name },
        ...(region.areas ? flattenRegions(region.areas) : []),
    ]);

export const Areas: FC = () => {
    const vrCampaignAreas = useSelector(selectVrCampaignAreas);
    const hasError = useSelector(selectVrCampaignErrors).areas;
    const vacancyAreaId = useSelector(selectVrCampaignVacancy).areaId;
    const regions = useSelector(selectRegions);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isMobile } = useBreakpoint();
    const tooltipRef = useRef(null);

    const [isTooltipVisible, setIsTooltipVisibleOn, setIsTooltipVisibleOff] = useOnOffState(false);
    const [inputValue, setInputValue] = useState('');

    const areaOptions = useMemo(() => flattenRegions(regions), [regions]);
    const dataProvider = useStaticDataProvider(areaOptions);

    const areaNameById = useMemo(() => new Map(areaOptions.map(({ value, text }) => [value, text])), [areaOptions]);

    const chipsState: ChipsTriggerState = useMemo(
        () => ({
            value: inputValue,
            chips: vrCampaignAreas.map((areaId) => ({
                value: String(areaId),
                label: areaNameById.get(String(areaId)) ?? String(areaId),
            })),
        }),
        [inputValue, vrCampaignAreas, areaNameById]
    );

    const handleChange = (newState: ChipsTriggerState) => {
        setInputValue(newState.value);
        if (newState.chips.length > 0) {
            dispatch(setVrCampaignErrors({ areas: false }));
        }
        const areaIds = newState.chips.map((chip) => Number(chip.value));
        dispatch(setVrCampaignAreas(areaIds));

        const isOnlyVacancyArea = areaIds.length === 1 && vacancyAreaId !== null && areaIds[0] === vacancyAreaId;

        let geoSource: GeoSource = 'added';
        if (areaIds.length === 0) {
            geoSource = 'None';
        } else if (isOnlyVacancyArea) {
            geoSource = 'from_vacancy';
        }

        vrPackageFormFieldTouch({
            fieldName: 'geo',
            geoCount: areaIds.length,
            geoIds: areaIds.join(','),
            geoSource,
        });
    };

    const validateAreas = () => {
        dispatch(setVrCampaignErrors({ areas: vrCampaignAreas.length === 0 }));
    };

    const emptySearchHint = (
        <Flex direction="column" align="center" gap={8} p={16} style={{ textAlign: 'center' }}>
            <Text typography="title-5-semibold">{trls(TrlKeys.emptyTitle)}</Text>
            <Text typography="paragraph-2-regular" style="secondary">
                {trls(TrlKeys.emptyDescription)}
            </Text>
        </Flex>
    );

    return (
        <div>
            <Flex direction="row" gap={6} align="center">
                <Text>{trls(TrlKeys.title)}</Text>
                <QuestionCircleOutlinedSize16
                    initialColor="secondary"
                    ref={tooltipRef}
                    onClick={setIsTooltipVisibleOn}
                    data-qa="vr-campaign-areas-tooltip"
                />
            </Flex>
            <VSpacing default={12} />
            <ChipsInput
                type="select"
                state={chipsState}
                onChange={handleChange}
                triggerProps={{
                    size: 'medium',
                    placeholder: trls(TrlKeys.placeholder),
                    invalid: hasError,
                    errorMessage: hasError ? trls(TrlKeys.error) : undefined,
                    onBlur: validateAreas,
                    'data-qa': 'vr-campaign-areas-input',
                }}
                selectProps={{
                    type: 'checkbox',
                    name: 'vr-campaign-areas',
                    dataProvider,
                    bottomSheetHeight: 'full-screen',
                    emptyOptionsSearchHint: emptySearchHint,
                    ...(isMobile && {
                        applyChangesButton: (
                            <Button mode="primary" style="accent" stretched data-qa="vr-campaign-areas-apply">
                                {trls(TrlKeys.apply)}
                            </Button>
                        ),
                        clearButton: (
                            <Button mode="secondary" style="accent" stretched data-qa="vr-campaign-areas-reset">
                                {trls(TrlKeys.reset)}
                            </Button>
                        ),
                    }),
                    renderHeader: ({
                        onCloseBottomSheet,
                        searchValue,
                        setSearchValue,
                        searchable,
                        onSearchSubmit,
                        optionsCount,
                        onCloseBottomSheetWithUpdateValues,
                    }) => (
                        <NavBar>
                            <NavBar.Pane>
                                <NavBar.Actions
                                    right={
                                        <CrossOutlinedSize24
                                            padding={8}
                                            initialColor="secondary"
                                            onClick={onCloseBottomSheet}
                                            data-qa="select-bottom-sheet-navigation-close"
                                        />
                                    }
                                >
                                    <NavBar.TitleContainer
                                        title={<Text typography="title-5-semibold">{trls(TrlKeys.title)}</Text>}
                                    />
                                </NavBar.Actions>
                                {searchable && (
                                    <>
                                        <VSpacing default={16} />
                                        <SearchInput
                                            clearable
                                            size="medium"
                                            value={searchValue}
                                            onChange={setSearchValue}
                                            onKeyDown={(event) => {
                                                if (onSearchSubmit && event.key === 'Enter') {
                                                    onSearchSubmit(
                                                        event,
                                                        onCloseBottomSheet,
                                                        optionsCount,
                                                        onCloseBottomSheetWithUpdateValues
                                                    );
                                                }
                                            }}
                                            placeholder={trls(TrlKeys.searchPlaceholder)}
                                            data-qa="vr-campaign-areas-search"
                                            autoComplete="off"
                                        />
                                        <VSpacing default={16} />
                                    </>
                                )}
                            </NavBar.Pane>
                        </NavBar>
                    ),
                }}
            />

            <TooltipCommon
                activatorRef={tooltipRef}
                tooltipContent={trls(TrlKeys.tooltip)}
                onClose={setIsTooltipVisibleOff}
                visible={isTooltipVisible}
                showClose
            />
        </div>
    );
};
